import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { formToObject } from 'lib/forms/formToObject'
import React, { FormEventHandler, useCallback, useContext, useRef } from 'react'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TextButton from 'LuxKit/Button/TextButton'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextInput from 'components/Common/Form/Input/TextInput'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useUserRegisterMutation from 'businessTraveller/hooks/useUserRegisterMutation'
import useBusinessTravellerCreditCustomerUpdateMutation from 'businessTraveller/hooks/useBusinessTravellerCreditCustomerUpdateMutation'
import { LUXURY_ESCAPES } from 'constants/brands'
import { BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS, BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_STEPS, BusinessTravellerPersonalAccountLinkingDispatchContext, BusinessTravellerPersonalAccountLinkingStateContext } from 'businessTraveller/contexts/BusinessTravellerPersonalAccountLinkingContexts'
import { BUSINESS_TRAVELLER_ACTIONS } from 'reducers/businessTravellerActionReducer'
import { useAppDispatch } from 'hooks/reduxHooks'

interface FormData {
  firstName: string
  lastName: string
  password: string
}

function BusinessTravellerNewPersonalUserSetupStep() {
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const {
    businessDetails,
    employeeProfile,
    personalEmail,
  } = useContext(BusinessTravellerPersonalAccountLinkingStateContext)
  const accountLinkingDispatch = useContext(BusinessTravellerPersonalAccountLinkingDispatchContext)
  const dispatch = useAppDispatch()

  const proceed = useCallback(() => {
    if (personalEmail) {
      dispatch({
        type: BUSINESS_TRAVELLER_ACTIONS.UPDATE_CREDIT_CUSTOMER_DETAILS,
        creditCustomerEmail: personalEmail,
      })
      accountLinkingDispatch({
        type: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS.SET_PERSONAL_EMAIL,
        personalEmail,
      })
      accountLinkingDispatch({
        type: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS.STEP_INTO,
        step: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_STEPS.PERSONAL_ACCOUNT_LINK_CONFIRMATION,
      })
    }
  }, [accountLinkingDispatch, dispatch, personalEmail])

  const creditCustomerIdMutation = useBusinessTravellerCreditCustomerUpdateMutation({
    onSuccess: proceed,
  })

  const accountRegistrationMutation = useUserRegisterMutation({
    // Credit accounts always belong to the personal Luxury Escapes tenant.
    brand: LUXURY_ESCAPES,
    onSuccess: (data) => {
      creditCustomerIdMutation.mutate({
        accessToken: data.accessToken,
        businessId: businessDetails.id,
      })
    },
  })

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    if (personalEmail) {
      const formData = formToObject<FormData>(event.currentTarget)

      accountRegistrationMutation.mutate({
        email: personalEmail,
        givenName: formData.firstName,
        surname: formData.lastName,
        password: formData.password,
      })
    }
  }, [accountRegistrationMutation, personalEmail])

  const goBack = useCallback(() => {
    accountLinkingDispatch({
      type: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS.STEP_INTO,
      step: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_STEPS.PERSONAL_EMAIL_UPDATE,
    })
  }, [accountLinkingDispatch])

  const isBusy = accountRegistrationMutation.isLoading || creditCustomerIdMutation.isLoading

  return <form onSubmit={handleSubmission}>
    <ModalHeader title="" onBackButtonClick={!isBusy ? goBack : undefined} />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={24}>
          <Group direction="vertical" gap={8}>
            <Heading variant="heading4">
              Welcome!
            </Heading>

            <BodyText variant="large">
              Looks like there isn't an existing Luxury Escapes profile for {personalEmail}. Let's set one up.
            </BodyText>
          </Group>

          <Group direction="vertical" gap={12}>
            <TextInput
              name="firstName"
              autoComplete="given-name"
              label="First name"
              required
              defaultValue={employeeProfile?.firstName}
              autoFocus
            />

            <TextInput
              name="lastName"
              autoComplete="family-name"
              label="Last name"
              required
              defaultValue={employeeProfile?.lastName}
            />

            <PasswordInput
              ref={passwordInputRef}
              name="password"
              autoComplete="new-password"
              label="Create password"
              minLength={8}
              required
            />

            <CheckboxInput name="hasAgreed" required>
              By continuing you agree to our Terms & Conditions and Privacy Policy and to subscribe to emails for offers, alerts and services.
            </CheckboxInput>

            {!!accountRegistrationMutation.error?.message && <MessageBanner
              description={accountRegistrationMutation.error.message}
              kind="critical"
            />}

            {!!creditCustomerIdMutation.error && <MessageBanner
              description={creditCustomerIdMutation.error}
              kind="critical"
            />}

            <TextButton
              type="submit"
              kind="primary"
              size="large"
              disabled={isBusy}
            >
              Continue
            </TextButton>
          </Group>
        </Group>
      </ModalContent>
    </ModalBody>
  </form>
}

export default BusinessTravellerNewPersonalUserSetupStep
